import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Component } from 'react';

import { OptionType } from '../../data/model';
import { ConfiguratorPageTranslation } from '../../localization/SiteTranslation';
import { IFeature } from '../../shared/common';
import TextButton from '../../shared/components/Button/TextButton';
import { TextLabelStrong } from '../../shared/components/Typography/TextLabels';
import { FabricCuratedSelectionBasedFilterState } from '../FabricCuratedSelectionBasedFilterState';
import { FabricOptionState } from '../FabricOptionState';
import { OptionState } from '../OptionState';
import Accessory from './AccessoriesConfigurator/Accessory/Accessory';
import { AccessoryState } from './AccessoriesConfigurator/AccessoryState';
import FabricCuratedSelectionBasedFilter from './FabricCuratedSelectionBasedFilter';
import Fabrics from './Fabrics';
import OptionAffectingPrice from './OptionAffectingPrice';
import OptionNotAffectingPrice from './OptionNotAffectingPrice';
import css from './OptionsConfigurator.css';

interface OptionsConfiguratorProps {
  translation: ConfiguratorPageTranslation;
  features: Array<IFeature>;
  brandId: string;
  curratedSelectionBasedFilter?: FabricCuratedSelectionBasedFilterState;
}

interface OptionsConfiguratorState {
  shouldDisplayFabricFilters: boolean;
}

@observer
class OptionsConfigurator extends Component<OptionsConfiguratorProps, OptionsConfiguratorState> {
  public constructor(props: OptionsConfiguratorProps) {
    super(props);

    this.state = {
      shouldDisplayFabricFilters: false,
    };
  }

  public toggleFiltersVisibility = () => {
    this.setState((state) => ({ shouldDisplayFabricFilters: !state.shouldDisplayFabricFilters }));
  };

  public renderOption(feature: IFeature) {
    const { type } = feature;
    const { translation, brandId } = this.props;

    if (feature.data.isDerived) {
      // Do not render the derive feature as it will be autoresolved
      return null;
    }

    if (feature.type === OptionType.Accessory) {
      return (
        <Accessory
          key={feature.id}
          accessory={feature as AccessoryState}
          translation={translation.accessories.accessory}
          className={!feature.enabled && css.hide}
        />
      );
    }

    return (
      <div key={feature.name} className={classNames(css.OptionsConfigurator, !feature.enabled && css.hide)}>
        <div className={css.header}>
          <TextLabelStrong className={css.name}>{feature.name}</TextLabelStrong>
          {type === OptionType.Fabric && (
            <>
              {(feature as FabricOptionState).anyFilterDefined && !(feature as FabricOptionState).useFacetFilters && (
                <TextButton className={css.buttonFilters} onClick={this.toggleFiltersVisibility}>
                  {this.state.shouldDisplayFabricFilters ? translation.hideFiltersText : translation.showFiltersText}
                </TextButton>
              )}
              <Fabrics
                translation={translation.options}
                option={feature as FabricOptionState}
                shouldDisplayFilters={this.state.shouldDisplayFabricFilters}
                brandId={brandId}
              />
            </>
          )}
        </div>
        {type === OptionType.NotAffectingPrice && <OptionNotAffectingPrice option={feature as OptionState} />}
        {type === OptionType.AffectingPrice && (
          <OptionAffectingPrice translation={translation.options} option={feature as OptionState} />
        )}
      </div>
    );
  }

  public render() {
    const { features, translation } = this.props;

    return (
      <>
        {this.props.curratedSelectionBasedFilter && (
          <FabricCuratedSelectionBasedFilter
            state={this.props.curratedSelectionBasedFilter}
            translation={translation.fabricCuratedSelectionBasedFilter}
          />
        )}
        {!features.empty() && features.map((x) => this.renderOption(x))}
      </>
    );
  }
}

export default OptionsConfigurator;
